import { useEffect, useRef } from "react";
import { Info, Pencil, Archive, FolderInput, Copy, Share2, Trash2 } from "lucide-react";

export interface MenuPosition {
  top: number;
  left: number;
}

interface ProjectContextMenuProps {
  position: MenuPosition;
  onClose: () => void;
  onInfo: () => void;
  onRename: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

interface ItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  comingSoon?: boolean;
  destructive?: boolean;
}

function MenuItem({ icon: Icon, label, onClick, disabled, comingSoon, destructive }: ItemProps) {
  const baseColor = destructive
    ? "text-destructive hover:bg-destructive/10"
    : "text-foreground hover:bg-muted";
  const cls = disabled
    ? "text-muted-foreground/50 cursor-not-allowed"
    : `cursor-pointer ${baseColor}`;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${cls}`}
    >
      <span className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
      {comingSoon && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground/70">
          soon
        </span>
      )}
    </button>
  );
}

export function ProjectContextMenu({
  position,
  onClose,
  onInfo,
  onRename,
  onArchive,
  onDelete,
}: ProjectContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{ top: position.top, left: position.left }}
      className="fixed z-50 w-52 rounded-lg border bg-popover p-1 shadow-md"
    >
      <MenuItem icon={Info} label="Project info" onClick={onInfo} />
      <MenuItem icon={Pencil} label="Rename" onClick={onRename} />
      <MenuItem icon={Archive} label="Archive" onClick={onArchive} />

      <div className="my-1 border-t" />

      <MenuItem icon={FolderInput} label="Move to folder" disabled comingSoon />
      <MenuItem icon={Copy} label="Duplicate" disabled comingSoon />
      <MenuItem icon={Share2} label="Share" disabled comingSoon />

      <div className="my-1 border-t" />

      <MenuItem icon={Trash2} label="Delete" onClick={onDelete} destructive />
    </div>
  );
}
